import { useEffect, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import customParseFormat from "dayjs/plugin/customParseFormat";

// dayjs.tz(value, format, zone) needs customParseFormat. Extending it here
// rather than relying on some unrelated module having already done it to the
// shared dayjs singleton.
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

const CLOSED_STATUSES = ["MAINTENANCE", "DISABLED", "OFFLINE"];

const secondsUntil = (target, now) =>
  target ? Math.max(Math.floor((dayjs(target).valueOf() - now.valueOf()) / 1000), 0) : 0;

// Pure evaluation of a Remote Config service window at a given moment.
// Everything the UI needs is derived from `now`, so the caller can re-run it on
// a timer and the answer changes by itself at the opening/closing boundary.
export const evaluateServiceConfig = (configValue, now = dayjs()) => {
  let config = {};

  try {
    config = { ...(JSON.parse(configValue) || {}) };
  } catch (error) {
    config = {};
  }

  if (config.timezone && config.hours) {
    // Anchor the HH:mm window to the day `now` falls on in the config's zone.
    // Without the date part dayjs.tz() would anchor it to the machine's current
    // date, so the window drifts whenever `now` is not today.
    const today = now.tz(config.timezone).format("YYYY-MM-DD");
    const atZone = (time) =>
      dayjs.tz(`${today} ${time}`, "YYYY-MM-DD HH:mm", config.timezone);

    config.startTime = atZone(config.hours.startTime);
    config.endTime = atZone(config.hours.endTime);
  }

  const hasWindow = Boolean(config.startTime && config.endTime);
  const isActivePeriod =
    hasWindow && now.isAfter(config.startTime) && now.isBefore(config.endTime);

  let serviceAvailable = false;

  switch (config.status) {
    case "ACTIVE":
      serviceAvailable = true;
      break;
    case "SCHEDULED_ACTIVE":
      serviceAvailable = isActivePeriod;
      if (!isActivePeriod && hasWindow) {
        // Closed for the day once we are past the end time — the next opening
        // is tomorrow's start.
        config.activeTime = now.isAfter(config.endTime)
          ? config.startTime.add(1, "day")
          : config.startTime;
      }
      break;
    default:
      serviceAvailable = false;
  }

  let secondsLeft = 0;
  if (!CLOSED_STATUSES.includes(config.status)) {
    if (config.activeTime) {
      // Counting down to the next opening.
      secondsLeft = secondsUntil(config.activeTime, now);
    } else if (config.endTime) {
      // Counting down to closing.
      secondsLeft = secondsUntil(config.endTime, now);
    }
  }

  return { config, serviceAvailable, secondsLeft };
};

// Re-evaluates every second so an open page flips itself at the boundary
// instead of staying enabled past closing time (or disabled past opening).
export const useServiceAvailability = (configValue) => {
  const [state, setState] = useState(() => evaluateServiceConfig(configValue));

  useEffect(() => {
    setState(evaluateServiceConfig(configValue));

    const interval = setInterval(() => {
      setState(evaluateServiceConfig(configValue));
    }, 1000);

    return () => clearInterval(interval);
  }, [configValue]);

  return state;
};
